import { Link, Outlet, useLocation } from 'react-router-dom';
import { LayoutDashboard, Mountain, Ticket, Trophy, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { BrandLogo } from '@/components/ui/BrandLogo';
import { PageTransition } from '@/components/ui/PageTransition';
export function MainLayout() {
    const location = useLocation();
    const { t } = useTranslation();

    const navigation = [
        { name: t('nav.home'), href: '/', icon: LayoutDashboard },
        { name: t('nav.races'), href: '/races', icon: Mountain },
        { name: t('nav.myBets'), href: '/my-bets', icon: Ticket },
        { name: t('nav.ranking'), href: '/ranking', icon: Trophy },
        { name: t('nav.profile'), href: '/profile', icon: User },
    ];

    const showMobileBottomNav = navigation.some(item => item.href === location.pathname);

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col md:flex-row">

            {/* --- SIDEBAR DESKTOP (Toujours là sur PC) --- */}
            <aside className="hidden md:flex w-64 flex-col bg-white border-r border-slate-100 fixed h-full z-20">
                <div className="p-6 border-b border-slate-50">
                    <BrandLogo size={40} />
                </div>

                <nav className="flex-1 p-4 space-y-2 overflow-y-auto" role="navigation" aria-label="Main navigation">
                    {navigation.map((item) => {
                        const isActive = location.pathname === item.href;
                        const Icon = item.icon;

                        return (
                            <Link
                                key={item.href}
                                to={item.href}
                                aria-current={isActive ? "page" : undefined}
                                className={cn(
                                    "flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-medium",
                                    isActive
                                        ? "bg-emerald-50 text-emerald-700 shadow-sm ring-1 ring-emerald-100"
                                        : "text-slate-500 hover:bg-slate-50 hover:text-slate-900"
                                )}
                            >
                                <Icon size={20} className={cn(isActive ? "text-emerald-600" : "text-slate-400")} />
                                {item.name}
                            </Link>
                        );
                    })}
                </nav>

                <div className="p-4 border-t border-slate-50 space-y-2">
                    <div className="flex justify-center gap-3">
                        <Link to="/legal" className="text-[10px] text-slate-400 hover:text-slate-600 transition-colors">{t('footer.legal')}</Link>
                        <Link to="/terms" className="text-[10px] text-slate-400 hover:text-slate-600 transition-colors">{t('footer.terms')}</Link>
                        <Link to="/privacy" className="text-[10px] text-slate-400 hover:text-slate-600 transition-colors">{t('footer.privacy')}</Link>
                    </div>
                    <p className="text-xs text-slate-400 text-center">
                        {t('footer.version')}
                    </p>
                </div>
            </aside>

            {/* --- CONTENT AREA --- */}
            <main className="flex-1 md:ml-64 flex flex-col min-h-screen relative w-full">

                {/* --- ZONE DE CONTENU --- */}
                <div className="flex-1 w-full mx-auto">
                    <PageTransition>
                        <Outlet />
                    </PageTransition>
                </div>

                {/* --- BOTTOM NAV MOBILE (Conditionnelle) --- */}
                {showMobileBottomNav && (
                    <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-100 px-4 py-1.5 flex justify-between items-center z-30 pb-safe" role="navigation" aria-label="Mobile navigation">
                        {navigation.map((item) => {
                            const isActive = location.pathname === item.href;
                            const Icon = item.icon;
                            return (
                                <Link
                                    key={item.href}
                                    to={item.href}
                                    aria-current={isActive ? "page" : undefined}
                                    className={cn(
                                        "flex flex-col items-center gap-0.5 p-1.5 rounded-xl transition-all min-w-[48px]",
                                        isActive ? "text-emerald-600" : "text-slate-400"
                                    )}
                                >
                                    <Icon size={22} strokeWidth={isActive ? 2.5 : 2} />
                                    <span className={cn("text-[10px]", isActive ? "font-bold" : "font-medium")}>
                                        {item.name}
                                    </span>
                                </Link>
                            )
                        })}
                    </nav>
                )}

            </main>
        </div>
    );
}
